import ArrowBack from "@mui/icons-material/ArrowBack";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useSetAtom } from "jotai";
import { type FC, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useBrandTones } from "../../../modules/brand-kit/hooks/use-brand-tones";
import type { ContentTone } from "../../../modules/brand-kit/server/brand-kit-service";
import { brandingAtom } from "../../../modules/brand-kit/store";
import { useGetBrandingBarDefaults } from "../../../modules/brand-kit/use-get-branding-bar-defaults";
import { BrandingBar } from "../../../modules/branding-bar/branding-bar";
import { CommsImageStyle } from "../../../modules/branding-bar/comms-image-style";
import { GenerateCommsButton } from "../../../modules/generate/generate-comms-button";
import { FixedFooter } from "../../../modules/generic/components/fixed-footer";
import { useAccount } from "../../../modules/generic/hooks/use-account";
import { Page } from "../../../modules/layout/components/page";
import { PageContent } from "../../../modules/layout/components/page-content";
import { useMultiformat } from "../../../modules/multiformat/hooks/use-multi-format";
import { CommsTemplatePreview } from "../../../modules/template-preview/comms-template-preview";
import {
  GenerateCommsContextProvider,
  useGenerateCommsContext,
} from "../../../modules/template-preview/generate-comms-context";
import { ThemeProvider } from "../../../styles/theme/theme-provider";
import type { UUID } from "../../../utils/uuid";
import { CommunicationHoldingDialog } from "../../create-content/communication-holding-dialog";
import { HeaderBarBurger } from "../../../modules/generic/components/header-bar-burger";

const GenerateCommsPage: FC = () => {
  const [searchParams] = useSearchParams();
  const multiformatLoader = useMultiformat();
  const { data: customBrandTones, loading: customBrandTonesLoading } = useBrandTones();

  const [brandingLoaded, setBrandingLoaded] = useState(false);
  const getBrandingBarDefaults = useGetBrandingBarDefaults();
  const setBranding = useSetAtom(brandingAtom);
  const { data: account } = useAccount();

  const brandToneId = searchParams.get("tone");
  const format = searchParams.get("format");
  const description = searchParams.get("q");
  const fileId = searchParams.get("file_id");
  const db = searchParams.get("db");
  const imageStyles = searchParams.get("image_styles")?.split(",");

  useEffect(() => {
    void getBrandingBarDefaults().then((defaults) => {
      if (defaults) {
        setBranding((prev) => ({
          ...prev,
          color: defaults.color,
          logo: defaults.logo,
          initialized: true,
        }));
      }
      setBrandingLoaded(true);
    });
  }, [getBrandingBarDefaults, setBranding]);

  if (multiformatLoader.error)
    return <Alert severity="error">Unable to load. Please try again later.</Alert>;

  if (
    customBrandTonesLoading ||
    multiformatLoader.loading ||
    !multiformatLoader.data ||
    !format ||
    !description ||
    !brandingLoaded ||
    !account
  )
    return null;

  return (
    <GenerateCommsContextProvider
      format={format}
      description={description}
      defaultBrandTone={brandToneId as UUID}
      customBrandTones={customBrandTones ?? []}
      multiformat={multiformatLoader.data}
      industry={account.industry ?? undefined}
      uploadedFileId={fileId ?? undefined}
      db={db ?? undefined}
      imageStyles={imageStyles ?? undefined}
    >
      <GenerateCommsPageInner contentTone={brandToneId ? "custom" : "professional"} />
    </GenerateCommsContextProvider>
  );
};

const GenerateCommsPageInner: FC<{ contentTone: ContentTone | "custom" }> = ({ contentTone }) => {
  const {
    format,
    previewImages: { images },
    description,
  } = useGenerateCommsContext();
  const [selectedContentTone, setSelectedContentTone] = useState<ContentTone | "custom">(
    contentTone,
  );
  const [pageReady, setPageReady] = useState(false);

  useEffect(() => {
    if (images.some((img) => img?.data?.url)) {
      queueMicrotask(() => {
        setPageReady(true);
      });
    }
  }, [images]);

  return (
    <ThemeProvider mode="light">
      <Header
        format={format}
        title={images?.[0]?.data?.title ? images[0].data.title : `Create a ${format} with AI`}
      />
      <Page title="Generate Communication">
        <PageContent centerContent>
          <CommsTemplatePreview
            contentTone={selectedContentTone}
            onContentToneChange={setSelectedContentTone}
          />
          {format && (
            <Box sx={{ justifyContent: "center" }}>
              <CommunicationHoldingDialog pageReady={pageReady} format={format} />
            </Box>
          )}
        </PageContent>
        {format === "Communication" ? (
          <FixedFooter
            noSidebar
            sx={{
              "& .fixedFooter--content": {
                display: "flex",
                flexDirection: "row-reverse",
                p: 2,
              },
            }}
          >
            <GenerateCommsButton contentTone={selectedContentTone} description={description} />
          </FixedFooter>
        ) : null}
      </Page>
    </ThemeProvider>
  );
};

export default GenerateCommsPage;

const Header: FC<{ format: string; title: string }> = ({ format, title }) => {
  const navigate = useNavigate();

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" p={2} bgcolor="white">
      <Stack direction="row">
        <HeaderBarBurger />
        <Stack data-testid="edit-content" pl={2} alignItems="flex-start">
          <Button
            sx={{ px: 0, py: "2px" }}
            data-analytics-id="edit-content-back"
            size="small"
            startIcon={<ArrowBack />}
            onClick={(e) => {
              e.preventDefault();
              if (window.history.length > 1) {
                void navigate(-1);
              } else {
                void navigate("/content-library");
              }
            }}
          >
            Back
          </Button>
          <Typography
            noWrap
            sx={{ fontSize: "1.1rem", fontWeight: 500, color: "text.primary" }}
            flexGrow={1}
          >
            {title}
          </Typography>
        </Stack>
      </Stack>
      <>
        {format.toLowerCase() === "communication" && <CommsImageStyle />}
        <BrandingBar compact />
      </>
    </Stack>
  );
};
